import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Check, ChevronDown, X } from 'lucide-react'

export type TaxonomyOption = { slug: string; label: string }

type Props = {
  /** Field label shown above the trigger */
  label: string
  options: TaxonomyOption[]
  selected: string[]
  onChange: (next: string[]) => void
  /** Shown inside the trigger when nothing is selected */
  placeholder?: string
  loading?: boolean
}

/**
 * Multi-select picker for taxonomy lists (topics, regions).
 * Trigger → Radix DropdownMenu with CheckboxItem per option.
 * Selected values shown as removable pills beneath the trigger.
 */
export function TaxonomyPicker({
  label,
  options,
  selected,
  onChange,
  placeholder = 'None selected',
  loading = false,
}: Props) {
  function toggle(slug: string) {
    onChange(
      selected.includes(slug)
        ? selected.filter(s => s !== slug)
        : [...selected, slug]
    )
  }

  function remove(slug: string) {
    onChange(selected.filter(s => s !== slug))
  }

  const triggerLabel =
    selected.length === 0
      ? placeholder
      : selected.length === 1
        ? (options.find(o => o.slug === selected[0])?.label ?? selected[0])
        : `${selected.length} selected`

  return (
    <div>
      <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
        {label}
      </label>

      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            disabled={loading}
            className="flex w-full items-center justify-between rounded border border-line bg-canvas px-3 py-2 text-xs text-fg transition-colors hover:border-line-strong disabled:opacity-50"
            aria-label={`Select ${label}`}
          >
            <span className={selected.length === 0 ? 'text-fg-secondary' : 'text-fg'}>
              {loading ? 'Loading…' : triggerLabel}
            </span>
            <ChevronDown size={12} className="shrink-0 text-fg-secondary" aria-hidden="true" />
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            sideOffset={4}
            align="start"
            className="z-50 max-h-72 w-[var(--radix-dropdown-menu-trigger-width)] overflow-y-auto rounded border border-line bg-canvas py-1 shadow-lg"
          >
            {options.map(option => (
              <DropdownMenu.CheckboxItem
                key={option.slug}
                checked={selected.includes(option.slug)}
                onCheckedChange={() => toggle(option.slug)}
                className="flex cursor-default items-center gap-2.5 px-3 py-1.5 text-xs text-fg outline-none hover:bg-fill focus:bg-fill"
              >
                {/* Custom checkbox indicator */}
                <span
                  className={[
                    'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border transition-colors',
                    selected.includes(option.slug)
                      ? 'border-accent-primary-solid bg-accent-primary-solid'
                      : 'border-line bg-canvas',
                  ].join(' ')}
                  aria-hidden="true"
                >
                  <DropdownMenu.ItemIndicator>
                    <Check size={9} className="text-white" strokeWidth={3} />
                  </DropdownMenu.ItemIndicator>
                </span>
                {option.label}
              </DropdownMenu.CheckboxItem>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      {/* Selected pills */}
      {selected.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-1" aria-label={`Selected ${label}`}>
          {selected.map(slug => {
            const option = options.find(o => o.slug === slug)
            return (
              <li key={slug}>
                <span className="flex items-center gap-1 rounded border border-line bg-fill px-2 py-0.5 text-[11px] text-fg">
                  {option?.label ?? slug}
                  <button
                    type="button"
                    onClick={() => remove(slug)}
                    className="ml-0.5 rounded text-fg-secondary transition-colors hover:text-fg"
                    aria-label={`Remove ${option?.label ?? slug}`}
                  >
                    <X size={10} aria-hidden="true" />
                  </button>
                </span>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
